import type { TailClipRecord } from "../application/tail-clip-runtime";
import type { SnakeMotionConfig } from "../config/gameplay-config";
import {
  getSnakeVisualDirection,
  type SnakeBodyVisualSegment,
  type SnakeFacePose,
  type SnakeVisualPoint,
} from "./snake-visual-renderer";

export interface SnakeMotionPhase {
  readonly blinkOffsetMs: number;
  readonly slitherOffsetRadians: number;
}

const fullTurnRadians = Math.PI * 2;

const getIdentifierHash = (identifier: string): number =>
  Array.from(identifier).reduce(
    (hash, character) => (Math.imul(hash, 31) + character.charCodeAt(0)) >>> 0,
    17,
  );

export const getSnakeMotionPhase = (
  identifier: string,
  config: SnakeMotionConfig,
): SnakeMotionPhase => {
  const hash = getIdentifierHash(identifier);
  const openCycleMs = config.blinkCycleMs - config.blinkDurationMs;
  return {
    blinkOffsetMs: config.blinkDurationMs + (hash % openCycleMs),
    slitherOffsetRadians: ((hash % 360) / 360) * fullTurnRadians,
  };
};

export const getSnakeChompStartedAtMs = (
  identifier: string,
  pickupAtMs: number | null,
  lastClip: TailClipRecord | null,
): number | null => {
  const clipAtMs =
    lastClip?.attackerId === identifier ? lastClip.occurredAtMs : null;
  if (pickupAtMs === null) {
    return clipAtMs;
  }
  if (clipAtMs === null) {
    return pickupAtMs;
  }
  return Math.max(pickupAtMs, clipAtMs);
};

export const getSnakeFacePose = (
  identifier: string,
  simulationTimeMs: number,
  chompStartedAtMs: number | null,
  motionScale: number,
  config: SnakeMotionConfig,
): SnakeFacePose => {
  if (motionScale === 0) {
    return { eyesClosed: false, mouthOpenScale: 0, tongueLengthScale: 1 };
  }
  const phase = getSnakeMotionPhase(identifier, config);
  const blinkTime =
    (simulationTimeMs + phase.blinkOffsetMs) % config.blinkCycleMs;
  const chompAge =
    chompStartedAtMs === null ? -1 : simulationTimeMs - chompStartedAtMs;
  const chompActive = chompAge >= 0 && chompAge < config.chompDurationMs;
  const chompCurve = chompActive
    ? Math.sin((chompAge / config.chompDurationMs) * Math.PI)
    : 0;
  return {
    eyesClosed: blinkTime < config.blinkDurationMs,
    mouthOpenScale: config.mouthOpenScale * chompCurve * motionScale,
    tongueLengthScale: 1 + config.tongueFlickScale * chompCurve * motionScale,
  };
};

export const getSnakeMotionSegments = (
  identifier: string,
  head: SnakeVisualPoint,
  segments: readonly SnakeBodyVisualSegment[],
  fallbackRadians: number,
  simulationTimeMs: number,
  motionScale: number,
  config: SnakeMotionConfig,
): readonly SnakeBodyVisualSegment[] => {
  const phase = getSnakeMotionPhase(identifier, config);
  return segments.map((segment, index) => {
    const leading = segments[index - 1]?.position ?? head;
    const trailing = segments[index + 1]?.position ?? segment.position;
    const direction = getSnakeVisualDirection(
      leading,
      trailing,
      fallbackRadians,
    );
    const frontRamp = (index + 1) / segments.length;
    const waveRadians =
      (simulationTimeMs / config.slitherCycleMs) * fullTurnRadians -
      (index / config.slitherWavelengthSegments) * fullTurnRadians +
      phase.slitherOffsetRadians;
    const offset =
      Math.sin(waveRadians) *
      segment.radius *
      config.slitherAmplitudeScale *
      motionScale *
      frontRamp;
    return {
      position: {
        x: segment.position.x + direction.sideX * offset,
        y: segment.position.y + direction.sideY * offset,
      },
      radius: segment.radius,
    };
  });
};
